import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import styles from "./PropertyStepperScreen.module.css";
import {
  StepSupportingDocuments,
  type StepSupportingDocumentsHandle,
} from "./StepSupportingDocuments";
import type { PropertyStepperScreenProps, StepDefinition } from "./PropertyStepperScreen.types";
import { toastService } from "@/shared/toast";

const defaultSteps = (stepsCount: number): StepDefinition[] =>
  Array.from({ length: stepsCount }, (_, idx) => ({
    id: `step-${idx + 1}`,
    label: `Step ${idx + 1}`,
  }));

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M15 18l-6-6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

export const PropertyStepperScreen = ({
  propertyType,
  propertyId = "",
  userId = "",
  currentStep,
  title,
  subtitle,
  onBack,
  onClose,
  onNext,
  onSkip,
  onSubmit,
  submitLabel = "Submit",
  children,
  footer,
  residentialSteps,
  commercialSteps,
  residentialStepsCount = 5,
  commercialStepsCount = 7,
  lastStepHasAnySupportingDocuments = false,
}: PropertyStepperScreenProps) => {
  const steps = useMemo<StepDefinition[]>(() => {
    if (propertyType === "RESIDENTIAL") {
      return residentialSteps ?? defaultSteps(residentialStepsCount);
    }
    if (propertyType === "COMMERCIAL") {
      return commercialSteps ?? defaultSteps(commercialStepsCount);
    }
    return [];
  }, [
    commercialSteps,
    commercialStepsCount,
    propertyType,
    residentialSteps,
    residentialStepsCount,
  ]);

  const stepsCount = steps.length;
  const safeCurrentStep = clamp(currentStep, 1, stepsCount);
  const isLastStep = safeCurrentStep >= stepsCount;
  const isFirstStep = safeCurrentStep === 1;
  const [supportingDocsLoading, setSupportingDocsLoading] = useState(false);
  const stepSupportingDocsRef = useRef<StepSupportingDocumentsHandle>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const activeStepDefinition = steps[safeCurrentStep - 1];
  const supportingDocumentType = activeStepDefinition?.supportingDocumentType;

  // Reset whenever we switch steps / document requirements.
  useEffect(() => {
    setSupportingDocsLoading(false);
    setSubmitAttempted(false);
  }, [supportingDocumentType]);

  const stepperTitleId = "property-stepper-title";

  const renderCircle = (stepNumber: number, stepLabel?: string) => {
    const isDone = stepNumber <= safeCurrentStep;
    return (
      <div
        key={stepNumber}
        className={[styles.circle, isDone ? styles.circleDone : styles.circleTodo]
          .filter(Boolean)
          .join(" ")}
        title={stepLabel ?? `Step ${stepNumber}`}
        aria-label={`${stepLabel ? `${stepLabel} - ` : ""}Step ${stepNumber}`}
        aria-current={stepNumber === safeCurrentStep ? "step" : undefined}
      >
        {stepNumber}
      </div>
    );
  };

  return (
    <div className={styles.screen}>
      <div className={styles.header}>
        {onBack ? (
          <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Go back">
            <BackIcon />
          </button>
        ) : (
          <div className={styles.backBtnSpacer} aria-hidden="true" />
        )}

        <div className={styles.titleGroup}>
          <h2 id={stepperTitleId} className={styles.title}>
            {title}
          </h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        {onClose ? (
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        ) : (
          <div className={styles.backBtnSpacer} aria-hidden="true" />
        )}
      </div>

      <div className={styles.stepper} role="list" aria-labelledby={stepperTitleId}>
        {steps.map((s, idx) => {
          const stepNumber = idx + 1;
          const isSegmentDone = stepNumber < safeCurrentStep;
          return (
            <Fragment key={s.id ?? `step-${stepNumber}`}>
              {renderCircle(stepNumber, s.label)}
              {idx < steps.length - 1 && (
                <div
                  className={[styles.segment, isSegmentDone ? styles.segmentDone : ""]
                    .filter(Boolean)
                    .join(" ")}
                  aria-hidden="true"
                />
              )}
            </Fragment>
          );
        })}
      </div>

      <div className={styles.content}>
        {children}
        <StepSupportingDocuments
          ref={stepSupportingDocsRef}
          propertyId={propertyId}
          userId={userId}
          documentType={supportingDocumentType}
          onDocumentsStateChange={(state) => {
            setSupportingDocsLoading(state.isLoading);
          }}
        />
      </div>
      {(onNext || footer) && (
        <div className={styles.footer}>
          {onNext ? (
            <div
              className={isLastStep ? styles.controlsColumn : styles.controls}
              aria-label="Step controls"
            >
              {isLastStep ? (
                <button
                  type="button"
                  className={styles.submitBtn}
                  disabled={supportingDocsLoading}
                  onClick={() => {
                    setSubmitAttempted(true);
                    if (!lastStepHasAnySupportingDocuments) {
                      toastService.show(
                        "Please upload at least one supporting document before submitting.",
                        "error"
                      );
                      return;
                    }
                    (onSubmit ?? onNext)?.();
                  }}
                >
                  {supportingDocsLoading ? "Please wait…" : submitLabel}
                </button>
              ) : isFirstStep ? (
                <button type="button" className={styles.nextBtn} onClick={() => onNext()}>
                  <span>START DOCUMENTATION</span>
                  <span className={styles.nextIcon} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 18l6-6-6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.skipBtn}
                    disabled={supportingDocsLoading}
                    onClick={() => {
                      stepSupportingDocsRef.current?.discardPending();
                      (onSkip ?? onNext)?.();
                    }}
                  >
                    SKIP FOR NOW
                  </button>
                  <button
                    type="button"
                    className={styles.nextBtn}
                    disabled={supportingDocsLoading}
                    onClick={() => {
                      void (async () => {
                        if (supportingDocumentType) {
                          const ok =
                            (await stepSupportingDocsRef.current?.commitAndCanAdvance()) ?? true;
                          if (!ok) return;
                        }
                        onNext?.();
                      })();
                    }}
                  >
                    <span>Next</span>
                    <span className={styles.nextIcon} aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M9 18l6-6-6-6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                </>
              )}

              {isLastStep &&
              !supportingDocsLoading &&
              !lastStepHasAnySupportingDocuments &&
              submitAttempted ? (
                <div className={styles.submitError} role="alert">
                  Upload at least one document to submit.
                </div>
              ) : null}
            </div>
          ) : null}

          {footer ? <div className={styles.footerCustom}>{footer}</div> : null}
        </div>
      )}
    </div>
  );
};
